<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>
    
    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-900">Crime Filters</h2>
          <button 
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Tabs -->
        <div class="border-b border-gray-200">
          <nav class="flex space-x-8 px-6" aria-label="Tabs">
            <button
              @click="activeTab = 'crime-types'"
              :class="[
                'py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'crime-types'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              Crime Types
            </button>
            
          </nav>
        </div>

        <!-- Content -->
        <div class="flex-1 flex overflow-hidden">
          <!-- Crime Types Tab -->
          <div v-if="activeTab === 'crime-types'" class="flex w-full">
            <!-- Left Sidebar - Categories -->
            <div class="w-48 bg-gray-50 border-r border-gray-200 overflow-y-auto">
              <div class="p-4 space-y-1">
                <button
                  class="w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors bg-blue-100 text-blue-700"
                >
                  All
                </button>
              </div>
            </div>

            <!-- Right Content - Metrics -->
            <div class="flex-1 flex flex-col">
              <!-- Search and Header -->
              <div class="p-6 border-b border-gray-200">
                <div class="relative mb-4">
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search crime types..."
                    class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                  <svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </div>

              </div>

              <!-- Metrics List -->
              <div class="flex-1 overflow-y-auto">
                <div class="space-y-0">
                  <div
                    v-for="crimeType in filteredMetrics"
                    :key="crimeType.value"
                    class="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div class="col-span-8 flex items-center space-x-3">
                      <input
                        type="checkbox"
                        :value="crimeType.value"
                        v-model="localSelectedCrimeTypes"
                        class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      >
                      <span class="text-sm text-gray-700">{{ crimeType.label }}</span>
                    </div>
                    <div class="col-span-4 text-right">
                      <span class="text-sm text-gray-500">-</span>
                    </div>
                  </div>
                </div>

                <!-- No results message -->
                <div v-if="filteredMetrics.length === 0" class="p-8 text-center text-gray-500">
                  <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0118 12M6 20.291A7.962 7.962 0 016 12m0 8.291zm12 0z"></path>
                  </svg>
                  <p class="text-sm">No Crime Types found matching your search.</p>
                </div>
              </div>
            </div>
          </div>

         
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            @click="closeModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="saveAndClose"
            class="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrimeFilterModal',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedCrimeTypes: {
      type: Array,
      default: () => []
    },
    availableCrimeTypes: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      activeTab: 'crime-types',
      searchQuery: '',
      localSelectedCrimeTypes: [...this.selectedCrimeTypes]
    }
  },
  
  computed: {
    filteredMetrics() {
      let crimeTypes = this.availableCrimeTypes;
      
      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        crimeTypes = crimeTypes.filter(crimeType => 
          crimeType.label.toLowerCase().includes(query) ||
          crimeType.value.toLowerCase().includes(query)
        );
      }
      
      return crimeTypes;
    }
  },
  
  watch: {
    selectedCrimeTypes: {
      handler(newValue) {
        this.localSelectedCrimeTypes = [...newValue];
      },
      deep: true
    },
    
    show(newValue) {
      if (newValue) {
        // Reset to defaults when modal opens
        this.activeTab = 'crime-types';
        this.searchQuery = '';
        this.localSelectedCrimeTypes = [...this.selectedCrimeTypes];
      }
    }
  },
  
  methods: {
    closeModal() {
      this.$emit('close');
    },
    
    saveAndClose() {
      this.$emit('update:selectedCrimeTypes', [...this.localSelectedCrimeTypes]);
      this.$emit('save', [...this.localSelectedCrimeTypes]);
      this.closeModal();
    }
  }
}
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Transition animations */
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>